import { useEffect, useState } from "react";
import { Bell, X, Share } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  pushPermission,
  enablePush,
  needsHomeScreenInstall,
  registerServiceWorker,
} from "@/lib/push";

const DISMISS_KEY = "dl_push_prompt_dismissed";

export default function PushPrompt() {
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // Register the SW early so pushes arrive even if the prompt is dismissed.
    registerServiceWorker();
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    if (needsHomeScreenInstall()) {
      setIos(true);
      setShow(true);
      return;
    }
    if (pushSupported() && pushPermission() === "default") setShow(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const turnOn = async () => {
    const res = await enablePush();
    if (res.status === "granted") {
      toast.success("Notifications on — we'll alert you on every dap, even when the app is closed.");
      dismiss();
    } else if (res.status === "needs-install") {
      toast.info("On iPhone: tap Share → Add to Home Screen, open it from there, then enable.");
    } else if (res.status === "denied") {
      toast.error("Notifications blocked. Turn them on in your browser settings.");
      dismiss();
    } else if (res.status === "server-disabled") {
      toast.error("Push is not configured on the server yet.");
    } else {
      toast.error("Couldn't enable notifications. Try again from Settings.");
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-prompt"
      className="bg-[#1B4D3E] text-[#F1E6D0]"
    >
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
        <Bell size={18} className="shrink-0" />
        {ios ? (
          <p className="text-sm flex-1">
            Get alerted on every dap on your iPhone — tap{" "}
            <Share size={13} className="inline -mt-0.5" /> <span className="font-semibold">Share → Add to Home Screen</span>,
            then open Dap Ledger from your home screen to turn on notifications.
          </p>
        ) : (
          <p className="text-sm flex-1">
            Never miss a dap — turn on notifications to get alerted the moment someone
            invites you or seals an agreement, even when the app is closed.
          </p>
        )}
        {!ios && (
          <button
            data-testid="push-prompt-enable-btn"
            onClick={turnOn}
            className="bg-[#F1E6D0] text-[#1B4D3E] rounded-md px-4 py-1.5 text-sm font-medium hover:bg-white transition-colors shrink-0"
          >
            Turn on
          </button>
        )}
        <button
          data-testid="push-prompt-dismiss-btn"
          onClick={dismiss}
          className="text-[#F1E6D0]/70 hover:text-white transition-colors shrink-0"
          title="Dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
